import { useEffect, useState, useRef } from 'react';

interface RobotAvatarProps {
  size?: number;
  isAnimated?: boolean;
}

export function RobotAvatar({ size = 64, isAnimated = true }: RobotAvatarProps) {
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const robotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAnimated) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!robotRef.current) return;

      const rect = robotRef.current.getBoundingClientRect();
      const robotCenterX = rect.left + rect.width / 2;
      const robotCenterY = rect.top + rect.height / 2;

      // Calculate angle to mouse
      const deltaX = e.clientX - robotCenterX;
      const deltaY = e.clientY - robotCenterY;
      const angle = Math.atan2(deltaY, deltaX);

      // Max distance eyes can move
      const maxDistance = 3;
      const eyeX = Math.cos(angle) * maxDistance;
      const eyeY = Math.sin(angle) * maxDistance;

      setEyePosition({ x: eyeX, y: eyeY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isAnimated]);

  const scale = size / 64; // Base size is 64px

  return (
    <div
      ref={robotRef}
      className="relative inline-block"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Antenna */}
        <line
          x1="32"
          y1="8"
          x2="32"
          y2="14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="text-primary"
        />
        <circle cx="32" cy="6" r="3" fill="currentColor" className="text-primary">
          <animate
            attributeName="fill-opacity"
            values="1;0.3;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Head */}
        <rect
          x="16"
          y="14"
          width="32"
          height="28"
          rx="6"
          fill="currentColor"
          className="text-primary"
        />

        {/* Eyes container */}
        <g>
          {/* Left Eye Background */}
          <rect
            x="21"
            y="22"
            width="10"
            height="8"
            rx="4"
            fill="white"
          />
          {/* Right Eye Background */}
          <rect
            x="33"
            y="22"
            width="10"
            height="8"
            rx="4"
            fill="white"
          />

          {/* Left Pupil */}
          <circle
            cx={26 + eyePosition.x}
            cy={26 + eyePosition.y}
            r="3"
            fill="#000000"
            className="transition-all duration-100"
          />
          {/* Right Pupil */}
          <circle
            cx={38 + eyePosition.x}
            cy={26 + eyePosition.y}
            r="3"
            fill="#000000"
            className="transition-all duration-100"
          />
        </g>

        {/* Mouth */}
        <path
          d="M 24 34 Q 32 37 40 34"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        >
          <animate
            attributeName="d"
            values="M 24 34 Q 32 37 40 34; M 24 34 Q 32 38 40 34; M 24 34 Q 32 37 40 34"
            dur="3s"
            repeatCount="indefinite"
          />
        </path>

        {/* Body */}
        <rect
          x="20"
          y="42"
          width="24"
          height="16"
          rx="4"
          fill="currentColor"
          className="text-primary opacity-90"
        />

        {/* Body Details */}
        <circle cx="28" cy="48" r="2" fill="white" opacity="0.6" />
        <circle cx="36" cy="48" r="2" fill="white" opacity="0.6" />
        <rect x="26" y="52" width="12" height="2" rx="1" fill="white" opacity="0.4" />

        {/* Arms */}
        <rect
          x="12"
          y="44"
          width="6"
          height="10"
          rx="3"
          fill="currentColor"
          className="text-primary opacity-80"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 15 49; -10 15 49; 0 15 49"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>
        <rect
          x="46"
          y="44"
          width="6"
          height="10"
          rx="3"
          fill="currentColor"
          className="text-primary opacity-80"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 49 49; 10 49 49; 0 49 49"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>

        {/* Legs */}
        <rect x="24" y="58" width="6" height="4" rx="2" fill="currentColor" className="text-primary opacity-70" />
        <rect x="34" y="58" width="6" height="4" rx="2" fill="currentColor" className="text-primary opacity-70" />
      </svg>

      {/* Glow effect */}
      {isAnimated && (
        <div
          className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"
          style={{ transform: `scale(${scale})` }}
        />
      )}
    </div>
  );
}
